import { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useHeader } from '../contexts/HeaderContext';
import { NavItem, standardNavItems, adminNavItems } from '../config/navConfig';

/**
 * Every stateful behavior previously tangled into Navbar.tsx's component body --
 * mobile-menu open state, scroll-shadow listener, admin-token localStorage polling +
 * cross-tab storage listener, dropdown hover-open/close with its 150ms delay, mobile
 * accordion toggle, active-path/active-child/active-dropdown-item matching, logout.
 * Zero JSX, zero theme-awareness -- shared unmodified by every theme's Navbar.
 */
export function useNavbarState() {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [adminUsername, setAdminUsername] = useState('');
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [mobileDropdownOpen, setMobileDropdownOpen] = useState<string | null>(null);
  const dropdownTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const location = useLocation();

  const { setShowAnnouncement, isAdmin, setIsAdmin, isAnnouncementVisible } = useHeader();

  useEffect(() => {
    const checkAdminStatus = () => {
      const adminToken = localStorage.getItem('adminToken');
      const storedUsername = localStorage.getItem('adminUsername');

      if (adminToken) {
        setIsAdmin(true);
        setShowAnnouncement(false);
        setAdminUsername(storedUsername || 'Admin');
      } else {
        setIsAdmin(false);
        if (adminUsername) {
          setAdminUsername('');
        }
      }
    };

    checkAdminStatus();

    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'adminToken' || e.key === 'adminUsername') {
        checkAdminStatus();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, adminUsername, setIsAdmin, setShowAnnouncement]);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleDropdownEnter = (itemName: string) => {
    if (dropdownTimeoutRef.current) {
      clearTimeout(dropdownTimeoutRef.current);
    }
    setActiveDropdown(itemName);
  };

  const handleDropdownLeave = () => {
    dropdownTimeoutRef.current = setTimeout(() => {
      setActiveDropdown(null);
    }, 150);
  };

  const toggleMobileDropdown = (itemName: string) => {
    setMobileDropdownOpen((current) => (current === itemName ? null : itemName));
  };

  const isActive = (path: string) => location.pathname === path;

  const isChildActive = (children?: NavItem[]) => {
    if (!children) return false;
    return children.some((child) => {
      const childPath = child.path.split('#')[0];
      return location.pathname === childPath;
    });
  };

  const isDropdownItemActive = (childPath: string) => {
    const hasHash = childPath.includes('#');
    const basePath = childPath.split('#')[0];
    const hash = hasHash ? childPath.split('#')[1] : null;

    if (hasHash) {
      return location.pathname === basePath && location.hash === `#${hash}`;
    }
    return location.pathname === basePath && !location.hash;
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminUsername');
    window.location.href = '/admin/login';
  };

  const navItems = isAdmin ? adminNavItems : standardNavItems;

  return {
    isOpen,
    setIsOpen,
    scrolled,
    adminUsername,
    activeDropdown,
    mobileDropdownOpen,
    isAdmin,
    isAnnouncementVisible,
    setShowAnnouncement,
    navItems,
    handleDropdownEnter,
    handleDropdownLeave,
    toggleMobileDropdown,
    isActive,
    isChildActive,
    isDropdownItemActive,
    handleLogout,
  };
}

export default useNavbarState;
